/*
 * Cobranza.js — Pantalla de Cobranza por mes (C-017).
 *
 * Ver diseno/CAMBIOS.md (C-017) y diseno/referencias/propuesta-cobranza.html.
 *   - Arriba, el mes elegido en la barra azul: Facturado → Cobrado → Por cobrar, y aparte el
 *     atrasado de meses anteriores (que no se suma al mes).
 *   - "Por mes": un renglón por mes del año; un clic elige ese mes.
 *   - La lista del mes con pestañas, buscador por nombre o monto y acciones a la vista.
 *   - Registrar pago (panel lateral): el depósito se aplica a los meses más antiguos y lo que
 *     sobra queda como saldo a favor. Deshacer desde el aviso o desde el historial.
 *
 * Las cifras salen del servidor (GET /api/cobranza/mes), que es quien las cuadra.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ClipboardList, Clock, Copy, Link, Paperclip, Pencil, Undo2, X } from 'lucide-react';
import { avisar, avisarError, confirmar } from './Dialogo';
import { BotonIcono } from './Tooltip';
import { fechaCorta, normalizarNombre } from './estados';
import {
  coincideMonto,
  formatoPesos,
  mesYaPaso,
  nombreMes,
  nombreMesAnio,
  parsearMonto,
  repartir,
} from './cobranzaLogica';

const PESTANAS = ['por-cobrar', 'atraso', 'todos', 'otros'];

const leerLocal = (clave, valor) => {
  try { return localStorage.getItem(clave) ?? valor; } catch (e) { return valor; }
};
const guardarLocal = (clave, valor) => {
  try { localStorage.setItem(clave, valor); } catch (e) { /* sin localStorage no se recuerda */ }
};

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const porcentaje = (parte, total) => (total > 0 ? Math.round((parte / total) * 100) : 0);

function Barrita({ valor }) {
  return (
    <span className="cob-barrita" aria-hidden="true">
      <i style={{ width: `${Math.min(100, Math.max(0, valor))}%` }} />
    </span>
  );
}

/**
 * props:
 *   fetchAPI, user, puedeAdministrar
 *   mes (1-12), anio, elegirMes(mes, anio)
 *   enviarLink(cobroId), adjuntarFactura(cobroId), copiarLink(cobroId) (también sin correo: se pega en WhatsApp)
 *   editarCliente(clienteId), nuevoCliente(), abrirCliente(clienteId) (C-023: el nombre abre la ficha)
 *   exportarExcel(), enviarATodos(), generandoExcel, enviandoATodos
 */
export default function Cobranza({
  fetchAPI, puedeAdministrar,
  mes, anio, elegirMes,
  enviarLink, adjuntarFactura, copiarLink, editarCliente, nuevoCliente, abrirCliente,
  exportarExcel, enviarATodos, generandoExcel, enviandoATodos,
}) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [pestana, setPestana] = useState('por-cobrar');
  const [busqueda, setBusqueda] = useState('');
  const [verPorMes, setVerPorMes] = useState(() => leerLocal('cobranzaPorMes', '1') === '1');
  const [menuMas, setMenuMas] = useState(false);
  const [pago, setPago] = useState(null); // { cliente, monto, fecha, guardando }
  const [historial, setHistorial] = useState(null); // { cliente, datos }
  const [honorarios, setHonorarios] = useState(null); // { cliente, valor }

  // fetchAPI cambia en cada render de App: se guarda en un ref para cargar solo cuando cambia el mes
  const fetchRef = useRef(fetchAPI);
  fetchRef.current = fetchAPI;

  const cargar = useCallback(async () => {
    try {
      const r = await fetchRef.current(`/cobranza/mes?mes=${mes}&anio=${anio}`);
      if (r && r.clientes) setDatos(r);
    } catch (e) {
      avisarError('cargar la cobranza del mes', e);
    } finally {
      setCargando(false);
    }
  }, [mes, anio]);

  useEffect(() => { setCargando(true); cargar(); }, [cargar]);

  // Esc cierra el menú "Más" y los paneles
  useEffect(() => {
    const alTeclear = (e) => {
      if (e.key !== 'Escape') return;
      setMenuMas(false);
      setPago(p => (p && !p.guardando ? null : p));
      setHistorial(null);
      setHonorarios(null);
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, []);

  const clientes = useMemo(() => datos?.clientes || [], [datos]);
  const regulares = useMemo(() => clientes.filter(c => !c.otros_ingresos), [clientes]);
  const mesPasado = mesYaPaso(mes, anio);
  const nombreDelMes = nombreMes(mes).toLowerCase();
  const nombreDelMesAnio = nombreMesAnio(mes, anio);

  const debeAlgo = (c) => (c.cobro?.por_cobrar || 0) > 0 || c.anteriores.monto > 0;

  const listas = useMemo(() => ({
    'por-cobrar': regulares.filter(c => (c.cobro?.por_cobrar || 0) > 0),
    atraso: regulares
      .filter(c => c.anteriores.meses > 0)
      .sort((a, b) => (a.anteriores.desde.anio * 12 + a.anteriores.desde.mes) - (b.anteriores.desde.anio * 12 + b.anteriores.desde.mes)),
    todos: regulares,
    otros: clientes.filter(c => c.otros_ingresos),
  }), [regulares, clientes]);

  // Buscador: por nombre, o por monto si lo escrito es un número
  const montoBuscado = parsearMonto(busqueda);
  const textoBuscado = normalizarNombre(busqueda.trim());
  const coincidencias = montoBuscado ? new Set(listas[pestana].filter(c => coincideMonto(c, montoBuscado)).map(c => c.id)) : null;
  let visibles = listas[pestana];
  if (montoBuscado) {
    visibles = [...visibles].sort((a, b) => (coincidencias.has(b.id) ? 1 : 0) - (coincidencias.has(a.id) ? 1 : 0));
  } else if (textoBuscado) {
    visibles = visibles.filter(c => normalizarNombre(c.nombre).includes(textoBuscado));
  }

  const totales = datos?.totales || { facturado: 0, cobrado: 0, por_cobrar: 0, clientes_por_cobrar: 0 };
  const atrasado = datos?.atrasado || { monto: 0, clientes: 0 };
  const atrasadosConCorreo = listas.atraso.filter(c => c.email && c.cobro_pendiente_id);

  // ───── Registrar pago ─────
  const abrirPago = (cliente) => {
    const sugerido = montoBuscado && coincideMonto(cliente, montoBuscado)
      ? montoBuscado
      : (cliente.pendientes || []).reduce((s, p) => s + p.por_cobrar, 0);
    setPago({ cliente, monto: sugerido ? String(Math.round(sugerido * 100) / 100) : '', fecha: hoyISO(), guardando: false });
  };

  const confirmarPago = async () => {
    const monto = parsearMonto(pago.monto);
    if (!monto) return;
    setPago(p => ({ ...p, guardando: true }));
    try {
      const r = await fetchAPI('/cobranza/deposito', {
        method: 'POST',
        body: JSON.stringify({ cliente_id: pago.cliente.id, monto, fecha: pago.fecha }),
      });
      const nombre = pago.cliente.nombre;
      setPago(null);
      await cargar();
      avisar({
        texto: `Pago de ${formatoPesos(monto)} registrado a ${nombre}.${r.saldo_a_favor > 0 ? ` ${formatoPesos(r.saldo_a_favor)} quedan a favor.` : ''}`,
        deshacer: async () => {
          try {
            await fetchAPI(`/cobranza/deposito/${r.id}/deshacer`, { method: 'POST' });
            await cargar();
            avisar({ texto: 'Se deshizo el pago.' });
          } catch (e) { avisarError('deshacer el pago', e); }
        },
      });
    } catch (e) {
      setPago(p => (p ? { ...p, guardando: false } : p));
      avisarError('registrar el pago', e);
    }
  };

  // ───── Historial ─────
  const abrirHistorial = async (cliente) => {
    setHistorial({ cliente, datos: null });
    try {
      const r = await fetchAPI(`/cobranza/cliente/${cliente.id}/historial`);
      setHistorial(h => (h && h.cliente.id === cliente.id ? { cliente, datos: r } : h));
    } catch (e) {
      setHistorial(null);
      avisarError('cargar el historial', e);
    }
  };

  const MOTIVO_DESHACER = { etiqueta: '¿Por qué se deshace?', ejemplo: 'Ej. el cliente no pagó; se registró por error' };

  // Meses que cubrió un depósito, con su nombre: "enero 2026, febrero 2026…"
  const mesesDelDeposito = (deposito) => {
    const porId = new Map(historial.datos.meses.map(m => [m.id, m]));
    return (deposito.aplicaciones || [])
      .map(a => porId.get(a.cobro_id))
      .filter(Boolean)
      .sort((a, b) => (a.anio * 12 + a.mes) - (b.anio * 12 + b.mes))
      .map(m => nombreMesAnio(m.mes, m.anio));
  };

  const deshacerDesdeHistorial = async (deposito, desdeMes = null) => {
    const meses = mesesDelDeposito(deposito);
    const saldo = deposito.saldo_a_favor > 0 ? ` y se quitan ${formatoPesos(deposito.saldo_a_favor)} de saldo a favor` : '';
    const varios = meses.length > 1;
    const motivo = await confirmar({
      titulo: desdeMes && !varios
        ? `¿Deshacer el pago de ${desdeMes}?`
        : `¿Deshacer el pago de ${formatoPesos(deposito.monto)}?`,
      cuerpo: desdeMes && varios
        ? `${desdeMes} se pagó con el depósito de ${formatoPesos(deposito.monto)} del ${fechaCorta(deposito.fecha)}, que cubrió ${meses.length} meses: ${meses.join(', ')}. Para quitar este pago se deshace el depósito completo: los ${meses.length} meses vuelven a quedar por cobrar${saldo}. Después registra solo lo que el cliente sí pagó.`
        : `${meses.length ? `${varios ? 'Los meses que cubría' : meses[0]} ${varios ? `(${meses.join(', ')}) vuelven` : 'vuelve'} a quedar por cobrar` : 'Los meses que cubría vuelven a quedar por cobrar'}${saldo}.`,
      motivo: MOTIVO_DESHACER,
      accion: desdeMes && varios ? 'Deshacer depósito' : 'Deshacer pago',
    });
    if (!motivo) return;
    try {
      await fetchAPI(`/cobranza/deposito/${deposito.id}/deshacer`, { method: 'POST', body: JSON.stringify({ motivo }) });
      avisar({ texto: 'Se deshizo el pago.' });
      await cargar();
      await abrirHistorial(historial.cliente);
    } catch (e) { avisarError('deshacer el pago', e); }
  };

  // Flecha "Deshacer pago" de un mes pagado (C-043): si lo pagó un depósito, se deshace ese depósito; si se marcó con la
  // versión anterior (sin depósito), el mes regresa solo a pendiente.
  const regresarMes = async (m) => {
    const nombre = nombreMesAnio(m.mes, m.anio);
    const conDeposito = m.pagos.find(p => p.tipo === 'deposito');
    if (conDeposito) {
      const deposito = historial.datos.depositos.find(d => d.id === conDeposito.pago_id);
      if (deposito) return deshacerDesdeHistorial(deposito, nombre);
    }
    const anterior = m.pagos.find(p => p.tipo === 'anterior');
    if (!anterior) return;
    const motivo = await confirmar({
      titulo: `¿Deshacer el pago de ${nombre}?`,
      cuerpo: `Se quita el pago de ${formatoPesos(anterior.monto)}${anterior.fecha ? ` del ${fechaCorta(anterior.fecha)}` : ''}${anterior.por ? ` (lo marcó ${anterior.por})` : ''}. ${nombre} vuelve a quedar por cobrar. Los demás meses no cambian.`,
      motivo: { etiqueta: '¿Por qué se regresa?', ejemplo: 'Ej. el cliente no pagó este mes; se marcó por error' },
      accion: 'Deshacer pago',
    });
    if (!motivo) return;
    try {
      await fetchAPI(`/cobranza/cobro/${m.id}/regresar`, { method: 'POST', body: JSON.stringify({ motivo }) });
      avisar({ texto: `${nombre} regresó a pendiente.` });
      await cargar();
      await abrirHistorial(historial.cliente);
    } catch (e) { avisarError('deshacer el pago', e); }
  };
  // Solo se puede regresar lo que se pagó con un depósito o con la versión anterior
  const sePuedeRegresar = (m) => m.pagos.some(p => p.tipo === 'deposito' || p.tipo === 'anterior');

  // ───── Editar honorarios (lápiz junto al monto) ─────
  const confirmarHonorarios = async () => {
    const nuevo = parsearMonto(honorarios.valor);
    if (nuevo === null) return;
    const { cliente } = honorarios;
    if (Math.round(nuevo * 100) === Math.round(cliente.honorarios * 100)) { setHonorarios(null); return; }
    const ok = await confirmar({
      titulo: `¿Cambiar los honorarios de ${cliente.nombre}?`,
      cuerpo: `De ${formatoPesos(cliente.honorarios)} a ${formatoPesos(nuevo)} al mes. Se usan desde el siguiente cobro; los meses ya generados no cambian.`,
      accion: 'Cambiar honorarios',
    });
    if (!ok) return;
    try {
      await fetchAPI(`/clientes/${cliente.id}/factura`, { method: 'PATCH', body: JSON.stringify({ factura_mensual: nuevo }) });
      setHonorarios(null);
      avisar({ texto: `Honorarios de ${cliente.nombre}: ${formatoPesos(nuevo)} al mes.` });
      await cargar();
    } catch (e) { avisarError('cambiar los honorarios', e); }
  };

  // ───── Recordar a los atrasados (C-010) ─────
  const recordarAtrasados = async () => {
    const sinCorreo = listas.atraso.length - atrasadosConCorreo.length;
    await confirmar({
      nivel: 3,
      titulo: `¿Recordar a ${atrasadosConCorreo.length} ${atrasadosConCorreo.length === 1 ? 'cliente' : 'clientes'} con atraso?`,
      cuerpo: (
        <>
          <p>Se manda por correo el link de pago de su mes más antiguo a quienes deben meses anteriores a {nombreDelMes}.</p>
          {sinCorreo > 0 && <p>{sinCorreo} {sinCorreo === 1 ? 'no tiene' : 'no tienen'} correo y no se les manda.</p>}
        </>
      ),
      accion: 'Enviar recordatorios',
      accionEnCurso: 'Enviando…',
      ejecutar: async (reportar) => {
        let enviados = 0;
        const fallidos = [];
        for (const [i, c] of atrasadosConCorreo.entries()) {
          reportar(`Enviando… ${i + 1} de ${atrasadosConCorreo.length}`);
          const cobroMasAntiguo = c.pendientes[0]?.cobro_id || c.cobro_pendiente_id;
          try {
            const r = await fetchAPI(`/pagos/enviar-cobro/${cobroMasAntiguo}`, { method: 'POST', body: JSON.stringify({}) });
            if (r?.success) enviados++; else fallidos.push(c.nombre);
          } catch (e) { fallidos.push(c.nombre); }
        }
        return {
          resultado: (
            <p>
              {enviados} {enviados === 1 ? 'recordatorio enviado' : 'recordatorios enviados'}.
              {fallidos.length > 0 && ` No se pudo enviar a: ${fallidos.join(', ')}.`}
            </p>
          ),
        };
      },
    });
  };

  if (cargando && !datos) {
    return <div className="cobranza-wrapper"><p className="cob-vacio">Cargando cobranza de {nombreDelMesAnio}…</p></div>;
  }

  const pctMes = porcentaje(totales.cobrado, totales.facturado);
  const conteos = Object.fromEntries(PESTANAS.map(p => [p, listas[p].length]));
  const textoPestana = {
    'por-cobrar': `Por cobrar de ${nombreDelMes}`,
    atraso: 'Con atraso',
    todos: 'Todos',
    otros: 'Otros ingresos',
  };

  const vistaPrevia = pago ? repartir(pago.cliente.pendientes, parsearMonto(pago.monto) || 0) : null;

  return (
    <div className="cobranza-wrapper cobranza-mes">
      <div className="cob-cabecera">
        <h2>Cobranza · {nombreDelMesAnio}</h2>
        <div className="cob-mas">
          <button
            type="button"
            className="cob-btn-sec"
            aria-haspopup="menu"
            aria-expanded={menuMas}
            onClick={() => setMenuMas(v => !v)}
          >
            Más <ChevronDown size={14} aria-hidden="true" />
          </button>
          {menuMas && (
            <>
              <div className="menu-overlay" onClick={() => setMenuMas(false)} />
              <div className="cob-menu" role="menu">
                <button type="button" role="menuitem" disabled={generandoExcel} onClick={() => { setMenuMas(false); exportarExcel(); }}>
                  {generandoExcel ? 'Generando Excel…' : 'Exportar a Excel'}
                </button>
                <button type="button" role="menuitem" disabled={enviandoATodos} onClick={() => { setMenuMas(false); enviarATodos(); }}>
                  {enviandoATodos ? 'Enviando links…' : 'Enviar link a todos'}
                </button>
                {puedeAdministrar && (
                  <button type="button" role="menuitem" onClick={() => { setMenuMas(false); nuevoCliente(); }}>Nuevo cliente de cobranza</button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Arriba: el mes elegido, y aparte lo atrasado */}
      <div className="cob-resumen">
        <section className="cob-tarjeta" aria-label={`Honorarios de ${nombreDelMes}`}>
          <h3>Honorarios de {nombreDelMes}</h3>
          <div className="cob-tres">
            <div><small>Facturado</small><b className="num">{formatoPesos(totales.facturado)}</b></div>
            <div className="cobrado"><small>Cobrado</small><b className="num">{formatoPesos(totales.cobrado)}</b></div>
            <div><small>Por cobrar</small><b className="num">{formatoPesos(totales.por_cobrar)}</b></div>
          </div>
          <div className="cob-avance">
            <Barrita valor={pctMes} />
            <span><b>{pctMes}%</b> cobrado · {totales.clientes_por_cobrar} {totales.clientes_por_cobrar === 1 ? 'cliente debe' : 'clientes deben'} algo de {nombreDelMes}</span>
          </div>
        </section>
        <section className={`cob-atraso${atrasado.monto > 0 ? '' : ' vacio'}`} aria-label={`Atrasado de meses anteriores a ${nombreDelMes}`}>
          <h3>Atrasado de meses anteriores a {nombreDelMes}</h3>
          <b className="num">{formatoPesos(atrasado.monto)}</b>
          <span>{atrasado.clientes} {atrasado.clientes === 1 ? 'cliente' : 'clientes'} · no se suma a {nombreDelMes}</span>
          {atrasado.monto > 0 && (
            <button type="button" className="cob-btn-link" onClick={() => setPestana('atraso')}>Ver quiénes →</button>
          )}
        </section>
      </div>

      {/* Por mes */}
      <section className="cob-por-mes" aria-label="Cobranza por mes">
        <div className="cob-por-mes-cab">
          <h3>Por mes · {anio}</h3>
          <span>Un clic en un mes lo elige</span>
          <button
            type="button"
            className="cob-btn-link"
            aria-expanded={verPorMes}
            onClick={() => { setVerPorMes(v => { guardarLocal('cobranzaPorMes', v ? '0' : '1'); return !v; }); }}
          >
            {verPorMes ? 'Ocultar' : 'Mostrar'}
          </button>
        </div>
        {verPorMes && (
          <table>
            <thead>
              <tr><th>Mes</th><th>Facturado</th><th>Cobrado</th><th>Por cobrar</th><th>Cobrado %</th></tr>
            </thead>
            <tbody>
              {(datos?.meses || []).length === 0 && (
                <tr><td colSpan={5} className="cob-vacio">Sin cobros en {anio}.</td></tr>
              )}
              {(datos?.meses || []).map(m => {
                const elegido = m.mes === mes && m.anio === anio;
                const pct = porcentaje(m.cobrado, m.facturado);
                return (
                  <tr
                    key={`${m.anio}-${m.mes}`}
                    className={`fila-mes${elegido ? ' elegido' : ''}`}
                    tabIndex={0}
                    aria-current={elegido ? 'true' : undefined}
                    onClick={() => elegirMes(m.mes, m.anio)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); elegirMes(m.mes, m.anio); } }}
                  >
                    <td>{nombreMes(m.mes)}</td>
                    <td className="num">{formatoPesos(m.facturado)}</td>
                    <td className="num">{formatoPesos(m.cobrado)}</td>
                    <td className={`num${m.por_cobrar > 0 && mesYaPaso(m.mes, m.anio) ? ' rojo' : ''}`}>{formatoPesos(m.por_cobrar)}</td>
                    <td><span className="cob-pct"><Barrita valor={pct} /> {pct}%</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* Lista de clientes del mes */}
      <div className="cob-herramientas">
        <div className="cob-pestanas" role="tablist" aria-label="Clientes">
          {PESTANAS.map(p => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={pestana === p}
              onClick={() => setPestana(p)}
            >
              {textoPestana[p]} <i>{conteos[p]}</i>
            </button>
          ))}
        </div>
        <div className="cob-buscar">
          <input
            type="search"
            placeholder="Buscar por nombre o monto…"
            aria-label="Buscar por nombre o monto"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="cob-btn-sec"
          disabled={atrasadosConCorreo.length === 0}
          onClick={recordarAtrasados}
          data-tooltip="Manda el link de pago de su mes más antiguo a quienes deben meses anteriores"
        >
          <Clock size={14} aria-hidden="true" /> Recordar a los atrasados ({atrasadosConCorreo.length})
        </button>
      </div>
      <p className="cob-pista" role="status">
        {montoBuscado && (coincidencias.size > 0
          ? <><b>{coincidencias.size}</b> {coincidencias.size === 1 ? 'cliente debe' : 'clientes deben'} exactamente {formatoPesos(montoBuscado)}: {coincidencias.size === 1 ? 'aparece' : 'aparecen'} arriba, resaltado{coincidencias.size === 1 ? '' : 's'}.</>
          : <>Ningún cliente debe exactamente {formatoPesos(montoBuscado)}. Puede ser un pago parcial: búscalo por nombre.</>)}
      </p>

      <div className="table-wrapper">
        <table className="data-table cobranza-table cob-tabla">
          <thead>
            <tr>
              <th>Cliente</th>
              <th className="r">Honorarios</th>
              <th className="r">Cobrado</th>
              <th className="r">Por cobrar</th>
              <th>{nombreMes(mes)}</th>
              <th>Meses anteriores</th>
              <th className="r">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visibles.length === 0 && (
              <tr><td colSpan={7} className="cob-vacio">
                {pestana === 'por-cobrar' ? `Nadie debe nada de ${nombreDelMes}.`
                  : pestana === 'atraso' ? 'Nadie debe meses anteriores.'
                  : 'Ningún cliente coincide.'}
              </td></tr>
            )}
            {visibles.map(c => {
              const resaltado = coincidencias?.has(c.id);
              const estado = c.cobro?.estado;
              return (
                <tr key={c.id} className={`${resaltado ? 'coincide' : ''}${c.activo ? '' : ' inactivo'}`}>
                  <td className="cob-cliente">
                    {puedeAdministrar
                      ? <button type="button" className="cob-nombre" onClick={() => (abrirCliente ? abrirCliente(c.id) : editarCliente(c.id))} data-tooltip={abrirCliente ? `${c.nombre} · Abrir ficha del cliente` : `Editar ${c.nombre}`}>{c.nombre}{abrirCliente ? ' ›' : ''}</button>
                      : <b>{c.nombre}</b>}
                    {c.ultimo_pago && <span>Último pago: {fechaCorta(c.ultimo_pago.fecha)}{c.ultimo_pago.por ? ` · por ${c.ultimo_pago.por}` : ''}</span>}
                    {!c.email && <span className="sin-correo">Sin correo</span>}
                    {!c.activo && <span className="sin-correo">Inactivo</span>}
                  </td>
                  <td className="r num">
                    <span className="cob-honorarios">
                      {formatoPesos(c.cobro ? c.cobro.facturado : c.honorarios)}
                      <BotonIcono
                        type="button"
                        className="cob-lapiz"
                        etiqueta={`Editar honorarios de ${c.nombre}`}
                       
                        onClick={() => setHonorarios({ cliente: c, valor: String(c.honorarios || '') })}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </BotonIcono>
                    </span>
                  </td>
                  <td className="r num">{c.cobro ? formatoPesos(c.cobro.cobrado) : '—'}</td>
                  <td className="r num">{c.cobro ? formatoPesos(c.cobro.por_cobrar) : '—'}</td>
                  <td>
                    {!c.cobro ? <span className="cob-gris">Sin cobro</span>
                      : estado === 'pagado' ? <span className="cob-chip pagado">Pagado</span>
                      : estado === 'parcial' ? <span className="cob-chip parcial">Parcial</span>
                      : <span className={`cob-chip ${mesPasado ? 'atrasado' : 'pendiente'}`}>Pendiente</span>}
                  </td>
                  <td>
                    {c.anteriores.meses > 0
                      ? <span className="cob-chip atrasado">{c.anteriores.meses} {c.anteriores.meses === 1 ? 'mes' : 'meses'} · {formatoPesos(c.anteriores.monto)}</span>
                      : c.saldo_a_favor > 0
                        ? <span className="cob-chip favor">A favor {formatoPesos(c.saldo_a_favor)}</span>
                        : <span className="cob-gris">—</span>}
                  </td>
                  <td>
                    <div className="cob-acciones">
                      {debeAlgo(c) && (
                        <button type="button" className="cob-btn-pri" onClick={() => abrirPago(c)}>Registrar pago</button>
                      )}
                      <button type="button" className="cob-btn-acc" onClick={() => abrirHistorial(c)} data-tooltip={`Historial de ${c.nombre}`}>
                        <ClipboardList size={16} aria-hidden="true" /> <span>Historial</span>
                      </button>
                      {c.cobro_pendiente_id && (
                        <button type="button" className="cob-btn-acc" onClick={() => copiarLink(c.cobro_pendiente_id)} data-tooltip="Copiar el link de pago para mandarlo por WhatsApp u otro medio">
                          <Copy size={16} aria-hidden="true" /> <span>Copiar link</span>
                        </button>
                      )}
                      {c.email && c.cobro_pendiente_id && (
                        <>
                          <button type="button" className="cob-btn-acc" onClick={() => enviarLink(c.cobro_pendiente_id)} data-tooltip={`Enviar link de pago a ${c.email}`}>
                            <Link size={16} aria-hidden="true" /> <span>Enviar link</span>
                          </button>
                          <button type="button" className="cob-btn-acc" onClick={() => adjuntarFactura(c.cobro_pendiente_id)} data-tooltip="Enviar el link con la factura PDF/XML adjunta">
                            <Paperclip size={16} aria-hidden="true" /> <span>Adjuntar factura</span>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {pestana === 'todos' && visibles.length > 0 && !textoBuscado && (
              <tr className="cob-total">
                <td>Total de {nombreDelMes}</td>
                <td className="r num">{formatoPesos(totales.facturado)}</td>
                <td className="r num">{formatoPesos(totales.cobrado)}</td>
                <td className="r num">{formatoPesos(totales.por_cobrar)}</td>
                <td colSpan={3} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Panel: Registrar pago */}
      {pago && (
        <div className="cob-velo" onClick={() => { if (!pago.guardando) setPago(null); }}>
          <aside className="cob-panel" role="dialog" aria-modal="true" aria-labelledby="titulo-pago" onClick={e => e.stopPropagation()}>
            <header>
              <div>
                <h3 id="titulo-pago">Registrar pago</h3>
                <p>{pago.cliente.nombre}</p>
              </div>
              <BotonIcono type="button" className="cob-cerrar" etiqueta="Cerrar" onClick={() => setPago(null)} disabled={pago.guardando}><X size={20} aria-hidden="true" /></BotonIcono>
            </header>
            <div className="cob-panel-cuerpo">
              <div className="cob-dos">
                <label className="cob-campo">
                  <span>Monto del depósito</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoFocus
                    value={pago.monto}
                    onChange={e => setPago(p => ({ ...p, monto: e.target.value }))}
                  />
                </label>
                <label className="cob-campo">
                  <span>Fecha del depósito</span>
                  <input type="date" value={pago.fecha} max={hoyISO()} onChange={e => setPago(p => ({ ...p, fecha: e.target.value }))} />
                </label>
              </div>
              {vistaPrevia && parsearMonto(pago.monto) && (
                <div className="cob-aplica">
                  <h4>Se aplica a</h4>
                  <ul>
                    {vistaPrevia.aplicaciones.map(a => (
                      <li key={a.cobro_id} className={a.completo ? 'cubre' : 'parcial'}>
                        <span>{a.completo && <span aria-hidden="true">✓ </span>}{nombreMesAnio(a.mes, a.anio)}</span>
                        <span className="num">{formatoPesos(a.aplicado)}{!a.completo && ` · faltan ${formatoPesos(a.falta)}`}</span>
                      </li>
                    ))}
                    {vistaPrevia.siguen.map(p => (
                      <li key={p.cobro_id} className="queda">
                        <span>{nombreMesAnio(p.mes, p.anio)}</span>
                        <span className="num">sigue pendiente · {formatoPesos(p.por_cobrar)}</span>
                      </li>
                    ))}
                  </ul>
                  {vistaPrevia.sobra > 0 && (
                    <p className="cob-sobra">Sobran {formatoPesos(vistaPrevia.sobra)}: quedan como saldo a favor y se descuentan del siguiente cobro.</p>
                  )}
                  <p className="cob-nota">Cada pago cuenta como cobrado del mes al que se aplica.</p>
                </div>
              )}
            </div>
            <footer>
              <button type="button" className="cob-btn-sec" onClick={() => setPago(null)} disabled={pago.guardando}>Cancelar</button>
              <button
                type="button"
                className="cob-btn-pri"
                disabled={!parsearMonto(pago.monto) || !pago.fecha || pago.guardando}
                onClick={confirmarPago}
              >
                {pago.guardando ? 'Registrando…' : `Registrar ${parsearMonto(pago.monto) ? formatoPesos(parsearMonto(pago.monto)) : 'pago'}`}
              </button>
            </footer>
          </aside>
        </div>
      )}

      {/* Panel: Historial */}
      {historial && (
        <div className="cob-velo" onClick={() => setHistorial(null)}>
          <aside className="cob-panel" role="dialog" aria-modal="true" aria-labelledby="titulo-historial" onClick={e => e.stopPropagation()}>
            <header>
              <div>
                <h3 id="titulo-historial">Historial</h3>
                <p>{historial.cliente.nombre}</p>
              </div>
              <BotonIcono type="button" className="cob-cerrar" etiqueta="Cerrar" onClick={() => setHistorial(null)}><X size={20} aria-hidden="true" /></BotonIcono>
            </header>
            <div className="cob-panel-cuerpo">
              {!historial.datos ? <p className="cob-vacio">Cargando…</p> : (
                <>
                  <section className="cob-hist">
                    <h4>Meses</h4>
                    {historial.datos.meses.length === 0 && <p className="cob-gris">Sin cobros registrados.</p>}
                    <ul>
                      {historial.datos.meses.map(m => (
                        <li key={m.id}>
                          <div className="cob-hist-mes">
                            <b>{nombreMesAnio(m.mes, m.anio)}</b>
                            <span className="cob-hist-monto">
                              {sePuedeRegresar(m) && (
                                <BotonIcono type="button" className="cob-hist-regresar" etiqueta={`Deshacer el pago de ${nombreMesAnio(m.mes, m.anio)}`} onClick={() => regresarMes(m)}>
                                  <Undo2 size={15} aria-hidden="true" />
                                </BotonIcono>
                              )}
                              <span className="num">{formatoPesos(m.honorarios)}</span>
                            </span>
                          </div>
                          {m.pagos.length === 0
                            ? <span className={mesYaPaso(m.mes, m.anio) ? 'cob-rojo' : 'cob-gris'}>pendiente</span>
                            : m.pagos.map((p, i) => (
                              <span key={i} className="cob-gris">
                                {formatoPesos(p.monto)}{p.fecha ? ` el ${fechaCorta(p.fecha)}` : ''}{p.por ? ` · por ${p.por}` : ''}{p.tipo === 'saldo_a_favor' ? ' · con saldo a favor' : ''}
                              </span>
                            ))}
                          {m.estado === 'parcial' && <span className="cob-aviso">faltan {formatoPesos(m.por_cobrar)}</span>}
                          {m.regreso && (
                            <span className="cob-gris cob-hist-motivo">
                              Regresado a pendiente el {fechaCorta(m.regreso.en)}{m.regreso.por ? ` · por ${m.regreso.por}` : ''}{m.regreso.motivo ? ` · “${m.regreso.motivo}”` : ''}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                  <section className="cob-hist">
                    <h4>Depósitos registrados</h4>
                    {historial.datos.depositos.length === 0 && <p className="cob-gris">Ninguno todavía.</p>}
                    <ul>
                      {historial.datos.depositos.map(d => (
                        <li key={d.id} className={d.deshecho ? 'deshecho' : ''}>
                          <div className="cob-hist-mes">
                            <span>{formatoPesos(d.monto)} el {fechaCorta(d.fecha)}{d.por ? ` · por ${d.por}` : ''}</span>
                            {d.deshecho
                              ? <span className="cob-gris">Deshecho</span>
                              : <button type="button" className="cob-btn-link" onClick={() => deshacerDesdeHistorial(d)}>Deshacer pago</button>}
                          </div>
                          {d.deshecho && (d.deshecho_por || d.motivo_deshacer) && (
                            <span className="cob-gris cob-hist-motivo">
                              Deshecho{d.deshecho_en ? ` el ${fechaCorta(d.deshecho_en)}` : ''}{d.deshecho_por ? ` · por ${d.deshecho_por}` : ''}{d.motivo_deshacer ? ` · “${d.motivo_deshacer}”` : ''}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                  <section className="cob-hist">
                    <h4>Saldo a favor · {formatoPesos(historial.datos.saldo_a_favor)}</h4>
                    {historial.datos.saldo_a_favor_movimientos.length === 0 && <p className="cob-gris">Sin movimientos.</p>}
                    <ul>
                      {historial.datos.saldo_a_favor_movimientos.map((m, i) => (
                        <li key={i}>
                          <div className="cob-hist-mes">
                            <span>{fechaCorta(m.fecha)}{m.por ? ` · por ${m.por}` : ''}</span>
                            <span className={`num ${m.monto < 0 ? '' : 'cob-verde'}`}>{m.monto < 0 ? `−${formatoPesos(-m.monto)} descontado` : `+${formatoPesos(m.monto)}`}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                </>
              )}
            </div>
          </aside>
        </div>
      )}

      {/* Diálogo: Editar honorarios */}
      {honorarios && (
        <div className="cob-velo centro" onClick={() => setHonorarios(null)}>
          <div className="cob-honorarios-dialogo" role="dialog" aria-modal="true" aria-labelledby="titulo-honorarios" onClick={e => e.stopPropagation()}>
            <h3 id="titulo-honorarios">Honorarios de {honorarios.cliente.nombre}</h3>
            <p className="cob-gris">Se usan para generar el cobro de cada mes. Los meses ya generados no cambian.</p>
            <label className="cob-campo">
              <span>Honorarios mensuales</span>
              <input
                type="text"
                inputMode="decimal"
                autoFocus
                value={honorarios.valor}
                onChange={e => setHonorarios(h => ({ ...h, valor: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') confirmarHonorarios(); }}
              />
            </label>
            <div className="cob-dialogo-botones">
              <button type="button" className="cob-btn-sec" onClick={() => setHonorarios(null)}>Cancelar</button>
              <button type="button" className="cob-btn-pri" disabled={parsearMonto(honorarios.valor) === null} onClick={confirmarHonorarios}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
